// File: oneportal-shell/src/api/services/manageDevicesServices.ts
// Service for the Manage Devices page.
// Fetches consumer accounts + devices from the RIS Public API using the
// Resideo IntegrationServicesClient (mirrors thermostat-pilet pattern),
// then flattens the response into a table-friendly row shape.
//
// Endpoint (hit by the SDK):
//   GET /api/v2/accounts?accountType={selectedContext}
//
// The `accountType` is read from localStorage (`oneportal_selected_context`),
// which is written by AuthContext / Context Selection after login. If the
// user hasn't picked a context yet, the query param is omitted and the SDK
// returns every account type the user has access to.
//
// Field mapping (API → row):
//   name         → consumerDevice.name
//   deviceModel  → device.product.productName
//   deviceType   → device.product.productFamily
//   location     → location.name
//   firmware     → blank ("") — not exposed by this endpoint
//   status       → hard-coded "online" — no live status flag in this response

import { createRisClient } from "../risClient";
import { getApiUrl } from "../apiConfig";
import { apiService } from "../../services/apiService";
import {
  DeviceServicesClient,
  type OpenAPIConfig as DeviceServicesOpenAPIConfig,
  type ThermostatStatusV2Response,
  type ThermostatConfigurationResponseV2,
} from "@resideo/web-device-services-api-client";
import {
  DeviceUnicornServicesClient,
  type OpenAPIConfig,
} from "@resideo/web-device-services-unicorn-api-client";

// ─── Domain types ────────────────────────────────────────────────────────────

export type DeviceStatus = "online" | "offline" | "privacy-mode";

export interface Device {
  id: string;
  name: string;
  deviceType: string;
  deviceModel: string;
  location: string;
  firmware: string;
  status: DeviceStatus;
  /** Raw camera family/model identifier (e.g. "Truman_CX6T", "Fenix_CX4D") — undefined for non-camera devices. */
  globalDeviceType?: string;
  configuration?: ThermostatConfigurationResponseV2;
}

/**
 * Product categories the Manage Devices page currently supports.
 *
 * This is an **intentional scope reduction**: the page's tabs are Cameras and
 * Thermostats, and only those two types have live-detail enrichment fetchers
 * (`fetchCameraDetails`, `fetchThermostatDetails`). Devices in other categories
 * (e.g. `SECURITY` panels, `WATER`) are dropped in `flattenDevices` so they
 * don't render as untyped rows with no working status/firmware column.
 *
 * To broaden the page later:
 *   • Add the category here
 *   • Add a corresponding tab in `ManageDevicesHeader`
 *   • Add an enrichment fetcher (or accept "—" status/firmware)
 *
 * Exported so tests + callers can override via `flattenDevices(res, categories)`.
 */
export const SUPPORTED_DEVICE_CATEGORIES: ReadonlySet<string> = new Set([
  "AIR", // Thermostats
  "VIDEO", // Cameras
]);

// The SDK's response type is inferred so we don't hand-write nested schemas.
type AccountsSdkResponse = Awaited<
  ReturnType<
    ReturnType<
      typeof createRisClient
    >["myAccounts"]["myAccountsGetMyConsumerAccountV2"]
  >
>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Synchronous token reader for the RIS SDK. The SDK requires a sync accessor,
 * so we read the token that `apiService` / Auth0 stash in localStorage after
 * each refresh.
 */
const getAccessToken = (): string | null =>
  localStorage.getItem("oneportal_auth_token") ??
  localStorage.getItem("authToken");

// Written by AuthContext when the user picks a context on the Context
// Selection page after login (values like "RESIDENTIAL", "COMMERCIAL",
// "PARTNER"). When absent, the SDK query is unfiltered.
const SELECTED_CONTEXT_KEY = "oneportal_selected_context";

/**
 * Flatten the nested accounts response into a flat `Device[]`.
 *
 * By default only devices in `SUPPORTED_DEVICE_CATEGORIES` (AIR, VIDEO) are
 * emitted — see that constant for the rationale. Pass a different set to
 * broaden or narrow the scope (e.g. for tests or a future page variant).
 *
 * Exported so it can be unit-tested independently of the network call.
 */
export const flattenDevices = (
  response: AccountsSdkResponse,
  allowedCategories: ReadonlySet<string> = SUPPORTED_DEVICE_CATEGORIES
): Device[] => {
  const devices: Device[] = [];
  const consumerUsers = response.data?.consumerUsers ?? [];
  consumerUsers.forEach((user) => {
    const locations = user.consumerAccount?.locations ?? [];
    locations.forEach((loc) => {
      (loc.consumerDevices ?? []).forEach((cd) => {
        const productCategories = cd.device?.product?.productCategories ?? [];
        const hasAllowedCategory = productCategories.some((category) =>
          allowedCategories.has(String(category))
        );

        if (!hasAllowedCategory) {
          return;
        }

        // Use deviceId from device as primary identifier (more stable than cd.id),
        // fall back to cd.id, and skip rows with no id to prevent collisions and
        // incorrect selection behavior.
        const id = cd.device?.deviceId ?? cd.id;
        if (!id) {
          return; // Skip this device
        }

        // Normalize deviceType. For VIDEO-category devices the productFamily
        // can vary (e.g. specific camera model names) — force the table's
        // deviceType to "Camera" so downstream filters, tabs, and the
        // camera-state enrichment call (`fetchCameraDetails`) always match.
        const productFamily = cd.device?.product?.productFamily;
        // `productCategories` is a typed enum on the SDK; compare as strings
        // so the literal "VIDEO" doesn't clash with `ProductCategory`.
        const isVideoCategory = productCategories.some(
          (category) => String(category) === "VIDEO"
        );
        const deviceType = isVideoCategory
          ? "Camera"
          : productFamily ?? "—";

        devices.push({
          id,
          name: cd.name ?? "Unnamed device",
          deviceModel: cd.device?.product?.productName ?? "—",
          deviceType,
          location: loc.name ?? "—",
          firmware: "",
          status: "online",
          globalDeviceType: cd.device?.globalDeviceType ?? undefined,
        });
      });
    });
  });
  return devices;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetch the current user's consumer devices for the selected context,
 * flattened for the Manage Devices table.
 *
 * Uses the Resideo `IntegrationServicesClient` (same pattern as
 * thermostat-pilet's `thermostatDashboardService`). Throws on network / auth
 * failure — callers surface the error via loading/error UI.
 *
 * The `accountType` query param is sourced from the same localStorage key
 * (`oneportal_selected_context`) written by AuthContext at login, so the
 * results match whatever context the user picked (RESIDENTIAL, COMMERCIAL,
 * PARTNER, …). If nothing is stored yet, the query is unfiltered.
 */
export const manageDevicesService = {
  async fetchDevices(accountType?: string): Promise<Device[]> {
    // Prime the auth token so `getAccessToken` (sync) can read it below.
    // If Auth0 hasn't populated storage yet, this awaits the refresh.
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const risClient = createRisClient(platformApiUrl, getAccessToken);

    // Explicit arg wins so callers can override; otherwise fall back to the
    // context the user picked at login.
    const effectiveAccountType =
      accountType ?? localStorage.getItem(SELECTED_CONTEXT_KEY) ?? undefined;

    const response =
      await risClient.myAccounts.myAccountsGetMyConsumerAccountV2(
        effectiveAccountType
      );

    return flattenDevices(response);
  },

  /**
   * Fetch live thermostat details for a single device and derive the fields
   * shown in the Manage Devices table (status + firmware).
   *
   * Uses the `DeviceServicesClient` SDK (same pattern as thermostat-pilet's
   * `getThermostatStatus`), so the exact URL is owned by the generated SDK —
   * we never hand-write the path here.
   *
   * SDK method: `client.devices.getApiV2DevicesThermostats(deviceId)`
   *   → GET /ris-public-api/api/v2/devices/thermostats/{deviceId}
   *
   * Mapping:
  *   status   → Reported.Online === true ? "online" : "offline"
   *   firmware → Reported.FirmwareSystem.SwPackage ?? "N/A"
   *
   * Throws on network/auth failure; callers decide how to handle it (typically
   * fall back to the row's current status/firmware).
   */
  async fetchThermostatDetails(
    deviceId: string
  ): Promise<{ status: DeviceStatus; firmware: string }> {
    // Ensure the auth token is primed for the SDK's sync token accessor.
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const config: Partial<DeviceServicesOpenAPIConfig> = {
      BASE: platformApiUrl,
      TOKEN: async () => {
        const token = getAccessToken();
        if (!token) {
          throw new Error("No access token available");
        }
        return token;
      },
    };
    const dsClient = new DeviceServicesClient(config);

    let response: ThermostatStatusV2Response;
    try {
      response = (await dsClient.devices.getApiV2DevicesThermostats(
        deviceId
      )) as ThermostatStatusV2Response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to load thermostat details for ${deviceId}: ${message}`,
      );
    }

    const reported = response?.Reported;
    const swPackage = reported?.FirmwareSystem?.SwPackage;

    return {
      status: reported?.Online === true ? "online" : "offline",
      firmware: swPackage && swPackage.length > 0 ? swPackage : "N/A",
    };
  },

  /**
   * Fetch the current configuration for a single thermostat. Used by the
   * Edit Controls page to derive the device's capabilities and bounds
   * (System Mode / Fan Mode options, allowed heat/cool setpoint ranges)
   * across the selected devices — it does NOT seed the form with current
   * values; the form defaults to a "Mixed" state until the user edits.
   *
   * SDK method: `client.devices.getApiV2DevicesThermostatsConfiguration(deviceId)`
   *   → GET /ris-public-api/api/v2/devices/thermostats/{deviceId}/configuration
   *
   * Throws on network/auth failure; callers decide how to handle it.
   */
  async fetchThermostatConfiguration(
    deviceId: string
  ): Promise<ThermostatConfigurationResponseV2> {
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const config: Partial<DeviceServicesOpenAPIConfig> = {
      BASE: platformApiUrl,
      TOKEN: async () => {
        const token = getAccessToken();
        if (!token) {
          throw new Error("No access token available");
        }
        return token;
      },
    };
    const dsClient = new DeviceServicesClient(config);

    try {
      const raw: unknown = await dsClient.devices.getApiV2DevicesThermostatsConfiguration(
        deviceId
      );
      // The RIS device-services endpoints often reply with the JSON body as
      // a text/plain string. Parse defensively so callers always see an
      // object with the expected shape (mirrors thermostat-pilet's
      // parseJsonResponse helper).
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) {
          throw new Error("empty response body");
        }
        return JSON.parse(trimmed) as ThermostatConfigurationResponseV2;
      }
      return raw as ThermostatConfigurationResponseV2;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to load thermostat configuration for ${deviceId}: ${message}`,
      );
    }
  },

  /**
   * Fetch live camera state for a single device and derive the fields shown
   * in the Manage Devices table (status + firmware).
   *
   * Endpoint: GET /ris-public-api/api/v1/devices/cameras/{deviceId}/state
   *
   * Mapping:
  *   status   → online === true ? "online" : "offline"
   *   firmware → version.softwareVersion ?? "N/A"
   *
   * Throws on network/auth failure; callers decide how to handle it (typically
   * fall back to the row's current status/firmware).
   */
  async fetchCameraDetails(
    deviceId: string
  ): Promise<{ status: DeviceStatus; firmware: string }> {
    // Ensure the auth token is primed for the SDK's sync token accessor.
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const config: Partial<OpenAPIConfig> = {
      BASE: platformApiUrl,
      TOKEN: async () => {
        const token = getAccessToken();
        if (!token) {
          throw new Error("No access token available");
        }
        return token;
      },
    };
    const unicornClient = new DeviceUnicornServicesClient(config);

    const response = await unicornClient.devices.getApiV1DevicesCamerasState(
      deviceId
    );

    // SDK may wrap the payload in `.data`; support both shapes.
    const state =
      (response as { data?: unknown })?.data ?? (response as unknown);
    const stateData = state as {
      online?: boolean | null;
      version?: { softwareVersion?: string | null } | null;
    };
    const softwareVersion = stateData?.version?.softwareVersion;

    return {
      status: stateData?.online === true ? "online" : "offline",
      firmware:
        softwareVersion && softwareVersion.length > 0 ? softwareVersion : "N/A",
    };
  },

  /**
   * Fetch the full camera state including settings.
   *
   * Endpoint: GET /ris-public-api/api/v1/devices/cameras/{deviceId}/state
   *
   * Returns the complete camera state including:
   *   - cameraSettings.advancedVideoConfig (recording behaviors)
   *   - cameraSettings.deterrenceConfig (deterrence settings)
   */
  async fetchCameraState(deviceId: string): Promise<{
    online: boolean;
    cameraSettings?: {
      advancedVideoConfig?: {
        mainStream?: { video: { geometry: { width: number; height: number }; rotate: boolean; quality: string; hevc: boolean; fps: number } };
        subStream?: { video: { geometry: { width: number; height: number }; rotate: boolean; quality: string; hevc: boolean; fps: number } };
        continuous?: { enabled: boolean; video: { geometry: { width: number; height: number }; rotate: boolean; quality: string; hevc: boolean; fps: number }; forceLowRes?: boolean };
        events?: { enabled: boolean; video: { geometry: { width: number; height: number }; rotate: boolean; quality: string; hevc: boolean; fps: number }; maxRecordingDurationSeconds?: number; postEventTimeoutSeconds?: number };
      };
      deterrenceConfig?: {
        activeDeterrence?: Array<{ name: string; lightPattern: string; sound: string; volume: number; offTime: number }>;
        panicDeterrenceName?: string;
        passiveDeterrence?: { enable: boolean };
      };
    };
  }> {
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const config: Partial<OpenAPIConfig> = {
      BASE: platformApiUrl,
      TOKEN: async () => {
        const token = getAccessToken();
        if (!token) {
          throw new Error("No access token available");
        }
        return token;
      },
    };
    const unicornClient = new DeviceUnicornServicesClient(config);

    const response = await unicornClient.devices.getApiV1DevicesCamerasState(
      deviceId
    );

    const state =
      (response as { data?: unknown })?.data ?? (response as unknown);
    return state as {
      online: boolean;
      cameraSettings?: {
        advancedVideoConfig?: any;
        deterrenceConfig?: any;
      };
    };
  },
};

export default manageDevicesService;
